import React, { useEffect, useState, useCallback } from 'react';
import {
  Card,
  Row,
  Col,
  Avatar,
  Tag,
  Statistic,
  Tabs,
  Descriptions,
  Button,
  Typography,
  Space,
  Breadcrumb,
  Spin,
  message,
  Modal,
  Form,
  Input,
  Select,
  Progress,
  Divider,
  Empty,
} from 'antd';
import {
  UserOutlined,
  YoutubeOutlined,
  InstagramOutlined,
  TwitterOutlined,
  MailOutlined,
  PlusOutlined,
  EditOutlined,
  LinkOutlined,
  HomeOutlined,
  SearchOutlined,
  BarChartOutlined,
  HistoryOutlined,
  RobotOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import { useParams, useNavigate, Link } from 'react-router-dom';
import type { Influencer, InfluencerDetailResponse, InfluencerEvaluation } from '../../types/scout';
import { scoutApi } from '../../services/scout';
import { ROUTES } from '../../config/routes';
import { COLORS } from '../../constants/colors';

const { Title, Text, Paragraph } = Typography;
const { TabPane } = Tabs;
const { Option } = Select;

export const InfluencerDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [influencer, setInfluencer] = useState<Influencer | null>(null);
  const [tags, setTags] = useState<string[]>([]);
  const [activeTab, setActiveTab] = useState('overview');

  // AI评估相关
  const [evaluation, setEvaluation] = useState<InfluencerEvaluation | null>(null);
  const [evaluating, setEvaluating] = useState(false);
  const [evaluationId, setEvaluationId] = useState<string>('');
  const [pollingInterval, setPollingInterval] = useState<ReturnType<typeof setInterval> | null>(null);

  // 标签编辑弹窗
  const [tagModalVisible, setTagModalVisible] = useState(false);
  const [tagForm] = Form.useForm();
  const [savingTags, setSavingTags] = useState(false);

  // 获取红人详情
  const fetchInfluencerDetail = useCallback(async (influencerId: string) => {
    setLoading(true);
    try {
      const res = await scoutApi.getInfluencer(influencerId);
      const data = res.data as InfluencerDetailResponse;
      setInfluencer(data.influencer);
      // 防御性处理：兼容 string[] 和 InfluencerTag[] 两种格式
      const rawTags = data.tags || [];
      const tagValues = rawTags.map((t: any) => typeof t === 'string' ? t : t.tag || '');
      setTags(tagValues.filter(Boolean));
    } catch (error) {
      message.error('获取红人详情失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (id) {
      fetchInfluencerDetail(id);
    }
  }, [id, fetchInfluencerDetail]);

  // 清理轮询
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        clearInterval(pollingInterval);
      }
    };
  }, [pollingInterval]);

  // 获取平台图标
  const getPlatformIcon = (platform?: string) => {
    switch (platform?.toLowerCase()) {
      case 'youtube':
        return <YoutubeOutlined />;
      case 'instagram':
        return <InstagramOutlined />;
      case 'tiktok':
        return <TwitterOutlined />;
      default:
        return <UserOutlined />;
    }
  };

  // 触发AI评估
  const handleTriggerEvaluation = async () => {
    if (!id) return;
    setEvaluating(true);
    try {
      const res = await scoutApi.evaluateInfluencer(id, {
        influencer_id: id,
        candidate_list_id: '', // 从详情页评估不需要候选列表ID
      });
      const evalData = res.data as InfluencerEvaluation;
      setEvaluationId(evalData.id || '');
      message.success('AI评估已启动');
      // 开始轮询
      startPolling(evalData.id || '');
    } catch (error) {
      message.error('启动评估失败');
      setEvaluating(false);
    }
  };

  // 轮询评估结果
  const startPolling = (evalId: string) => {
    if (pollingInterval) {
      clearInterval(pollingInterval);
    }

    const interval = setInterval(async () => {
      try {
        const res = await scoutApi.getEvaluation(evalId);
        const evalData = res.data as InfluencerEvaluation;
        setEvaluation(evalData);

        if (evalData.status === 'completed') {
          clearInterval(interval);
          setPollingInterval(null);
          setEvaluating(false);
          message.success('评估完成');
        } else if (evalData.status === 'failed') {
          clearInterval(interval);
          setPollingInterval(null);
          setEvaluating(false);
          message.error('评估失败');
        }
      } catch (error) {
        clearInterval(interval);
        setPollingInterval(null);
        setEvaluating(false);
        message.error('获取评估结果失败');
      }
    }, 3000);

    setPollingInterval(interval);
  };

  // 保存标签
  const handleSaveTags = async () => {
    if (!id) return;
    const values = tagForm.getFieldsValue();
    setSavingTags(true);
    try {
      await scoutApi.updateInfluencerTags(id, { tags: values.tags || [] });
      setTags(values.tags || []);
      message.success('标签更新成功');
      setTagModalVisible(false);
    } catch (error) {
      message.error('标签更新失败');
    } finally {
      setSavingTags(false);
    }
  };

  // 打开标签编辑弹窗
  const handleOpenTagModal = () => {
    tagForm.setFieldsValue({ tags });
    setTagModalVisible(true);
  };

  // 获取评估状态标签
  const getEvaluationStatusTag = (status?: string) => {
    switch (status) {
      case 'completed':
        return <Tag color="green">已完成</Tag>;
      case 'evaluating':
      case 'pending':
      case 'processing':
        return <Tag color="orange">评估中</Tag>;
      case 'failed':
        return <Tag color="red">失败</Tag>;
      default:
        return <Tag>未评估</Tag>;
    }
  };

  // 渲染数据概览Tab
  const renderOverviewTab = () => (
    <div>
      <Row gutter={24}>
        <Col span={6}>
          <Card>
            <Statistic
              title="总粉丝数"
              value={influencer?.follower_count || 0}
              formatter={(value) => `${(Number(value) / 10000).toFixed(1)}万`}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="平均互动率"
              value={influencer?.engagement_rate ? influencer.engagement_rate * 100 : 0}
              suffix="%"
              precision={2}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="30天平均播放量"
              value={influencer?.avg_views_30d || 0}
              formatter={(value) => `${(Number(value) / 10000).toFixed(1)}万`}
            />
          </Card>
        </Col>
        <Col span={6}>
          <Card>
            <Statistic
              title="合作次数"
              value={influencer?.cooperation_count || 0}
            />
          </Card>
        </Col>
      </Row>
      <Divider />
      <Descriptions bordered column={2}>
        <Descriptions.Item label="平台">
          <Tag icon={getPlatformIcon(influencer?.platform)}>
            {influencer?.platform?.toUpperCase?.() || influencer?.platform}
          </Tag>
        </Descriptions.Item>
        <Descriptions.Item label="国家/地区">
          {influencer?.country || '-'}
        </Descriptions.Item>
        <Descriptions.Item label="频道链接" span={2}>
          {influencer?.channel_url ? (
            <a href={influencer.channel_url} target="_blank" rel="noopener noreferrer">
              <LinkOutlined /> 访问频道
            </a>
          ) : (
            '-'
          )}
        </Descriptions.Item>
        <Descriptions.Item label="内容分类" span={2}>
          <Space>
            {influencer?.content_categories?.map((cat) => (
              <Tag key={cat}>{cat}</Tag>
            )) || '-'}
          </Space>
        </Descriptions.Item>
        <Descriptions.Item label="最后合作时间">
          {influencer?.last_cooperation_at
            ? new Date(influencer.last_cooperation_at).toLocaleDateString('zh-CN')
            : '无'}
        </Descriptions.Item>
        <Descriptions.Item label="数据更新时间">
          {influencer?.updated_at
            ? new Date(influencer.updated_at).toLocaleDateString('zh-CN')
            : '-'}
        </Descriptions.Item>
      </Descriptions>
    </div>
  );

  // 渲染AI评估Tab
  const renderEvaluationTab = () => (
    <div>
      <div style={{ marginBottom: 24 }}>
        <Space>
          <Button
            type="primary"
            icon={<RobotOutlined />}
            onClick={handleTriggerEvaluation}
            loading={evaluating}
            disabled={evaluating}
          >
            {evaluating ? '评估中...' : '触发AI评估'}
          </Button>
          {evaluation && (
            <Button icon={<ReloadOutlined />} onClick={() => evaluation.id && startPolling(evaluation.id)}>
              刷新结果
            </Button>
          )}
        </Space>
      </div>

      {evaluating && !evaluation?.overall_score && (
        <Card style={{ textAlign: 'center', padding: '48px 0' }}>
          <Spin size="large" />
          <p style={{ marginTop: 16 }}>AI正在评估红人数据，请稍候...</p>
          <Text type="secondary">每3秒自动刷新状态</Text>
        </Card>
      )}

      {evaluation?.overall_score !== undefined && (
        <Card title="评估结果" style={{ marginBottom: 24 }}>
          <Row gutter={24}>
            <Col span={8}>
              <div style={{ textAlign: 'center' }}>
                <Title level={2} style={{ color: COLORS.primary }}>
                  {evaluation.overall_score}
                </Title>
                <Text>综合评分</Text>
              </div>
            </Col>
            <Col span={16}>
              <div style={{ marginBottom: 16 }}>
                <Text strong>评估状态：</Text>
                {getEvaluationStatusTag(evaluation.status)}
              </div>
              {evaluation.data_confidence && (
                <div style={{ marginBottom: 16 }}>
                  <Text strong>数据可信度：</Text>
                  <Tag color={evaluation.data_confidence === 'high' ? 'green' : evaluation.data_confidence === 'medium' ? 'orange' : 'red'}>
                    {evaluation.data_confidence === 'high' ? '高' : evaluation.data_confidence === 'medium' ? '中' : '低'}
                  </Tag>
                </div>
              )}
              {evaluation.rank && (
                <div>
                  <Text strong>排名：</Text>
                  <Text>第 {evaluation.rank} 名</Text>
                </div>
              )}
            </Col>
          </Row>
        </Card>
      )}

      {evaluation?.dimension_scores && (
        <Card title="维度评分" style={{ marginBottom: 24 }}>
          {Object.entries(evaluation.dimension_scores).map(([key, value]: [string, unknown]) => (
            <div key={key} style={{ marginBottom: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                <Text>{key}</Text>
                <Text strong>{value as number}分</Text>
              </div>
              <Progress percent={Number(value) * 10} showInfo={false} strokeColor={COLORS.primary} />
            </div>
          ))}
        </Card>
      )}

      {evaluation?.match_analysis && (
        <Card title="匹配分析" style={{ marginBottom: 24 }}>
          <Paragraph>{evaluation.match_analysis}</Paragraph>
        </Card>
      )}

      {evaluation?.recommendation_reason && (
        <Card title="推荐理由">
          <Paragraph>{evaluation.recommendation_reason}</Paragraph>
        </Card>
      )}

      {!evaluation && !evaluating && (
        <Empty description="暂无评估数据，点击上方按钮开始评估" />
      )}
    </div>
  );

  // 渲染合作历史Tab
  const renderHistoryTab = () => (
    <Empty description="合作历史功能开发中..." />
  );

  if (!influencer && !loading) {
    return (
      <Card>
        <Empty description="红人不存在或已被删除" />
      </Card>
    );
  }

  return (
    <div>
      {/* 面包屑导航 */}
      <Breadcrumb style={{ marginBottom: 16 }}>
        <Breadcrumb.Item>
          <Link to={ROUTES.HOME}>
            <HomeOutlined /> 首页
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          <Link to={ROUTES.SCOUT_SEARCH}>
            <SearchOutlined /> 红人搜索
          </Link>
        </Breadcrumb.Item>
        <Breadcrumb.Item>红人详情</Breadcrumb.Item>
      </Breadcrumb>

      <Title level={4} style={{ marginBottom: 24 }}>
        红人详情
      </Title>

      {/* 基本信息卡片 */}
      <Card loading={loading} style={{ marginBottom: 24 }}>
        <Row gutter={24}>
          <Col span={6}>
            <div style={{ textAlign: 'center' }}>
              <Avatar size={120} icon={getPlatformIcon(influencer?.platform)} />
              <Title level={5} style={{ marginTop: 16 }}>
                {influencer?.name || '-'}
              </Title>
              <Space>
                <Tag icon={getPlatformIcon(influencer?.platform)}>
                  {influencer?.platform?.toUpperCase?.() || influencer?.platform}
                </Tag>
                {influencer?.country && <Tag>{influencer.country}</Tag>}
              </Space>
            </div>
          </Col>
          <Col span={18}>
            <Row gutter={16}>
              <Col span={6}>
                <Statistic
                  title="粉丝数"
                  value={influencer?.follower_count || 0}
                  formatter={(value) => `${(Number(value) / 10000).toFixed(1)}万`}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="平均互动率"
                  value={influencer?.engagement_rate ? influencer.engagement_rate * 100 : 0}
                  suffix="%"
                  precision={2}
                />
              </Col>
              <Col span={6}>
                <Statistic
                  title="平均播放量"
                  value={influencer?.avg_views_30d || 0}
                  formatter={(value) => `${(Number(value) / 10000).toFixed(1)}万`}
                />
              </Col>
              <Col span={6}>
                <Statistic title="合作次数" value={influencer?.cooperation_count || 0} />
              </Col>
            </Row>

            {/* 标签管理 */}
            <div style={{ marginTop: 24 }}>
              <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
                <Text strong style={{ marginRight: 8 }}>标签：</Text>
                <Button type="link" icon={<EditOutlined />} onClick={handleOpenTagModal} size="small">
                  编辑
                </Button>
              </div>
              <Space>
                {tags.length > 0 ? (
                  tags.map((tag) => <Tag key={tag}>{tag}</Tag>)
                ) : (
                  <Text type="secondary">暂无标签</Text>
                )}
              </Space>
            </div>

            <div style={{ marginTop: 24 }}>
              <Space>
                <Button type="primary" icon={<MailOutlined />}>
                  发送邀约
                </Button>
                <Button icon={<PlusOutlined />}>添加到列表</Button>
                {influencer?.channel_url && (
                  <Button icon={<LinkOutlined />} href={influencer.channel_url} target="_blank">
                    访问频道
                  </Button>
                )}
              </Space>
            </div>
          </Col>
        </Row>
      </Card>

      {/* Tabs */}
      <Card>
        <Tabs activeKey={activeTab} onChange={setActiveTab}>
          <TabPane
            tab={<span><BarChartOutlined /> 数据概览</span>}
            key="overview"
          >
            {renderOverviewTab()}
          </TabPane>
          <TabPane
            tab={<span><RobotOutlined /> AI评估</span>}
            key="evaluation"
          >
            {renderEvaluationTab()}
          </TabPane>
          <TabPane
            tab={<span><HistoryOutlined /> 合作历史</span>}
            key="history"
          >
            {renderHistoryTab()}
          </TabPane>
        </Tabs>
      </Card>

      {/* 标签编辑弹窗 */}
      <Modal
        title="编辑标签"
        open={tagModalVisible}
        onOk={handleSaveTags}
        onCancel={() => setTagModalVisible(false)}
        confirmLoading={savingTags}
      >
        <Form form={tagForm} layout="vertical">
          <Form.Item name="tags" label="标签">
            <Select
              mode="tags"
              placeholder="输入标签后按回车添加"
              style={{ width: '100%' }}
              tokenSeparators={[',']}
            >
              {['科技', '美妆', '时尚', '游戏', '美食', '旅行', '健身', '教育'].map((tag) => (
                <Option key={tag} value={tag}>
                  {tag}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};
